import { Col, Container, Row } from 'react-bootstrap'
import { FileHandler } from '../../../../components/FileHandler/FileHandler'
import React, { useEffect, useState } from 'react'
import { FileKind } from '../../../../models/File'
import { FileInfo } from '../../../../models/Account'
import { useResetLogoFileMutation, useUpdateLogoFileMutation } from '../../../../store/account/account'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import imageIcon from '../../../../assets/images/image-icon.svg?url'
import ZocksLogoFullBlack from '../../../../assets/images/ZocksLogoFullBlack.png'

type LogoSettingsProps = {}

const ACCOUNT_SCOPE = {
  scope: 'account:update',
  superScope: ['account:admin'],
}

const LOGO_MIN_RESOLUTION = { minWidth: 160, minHeight: 160 }
const _5_MB_IN_BYTES = 5 * 1024 * 1024

const DEFAULT_FILE = {
  url: ZocksLogoFullBlack,
  text: 'Default logo image',
}

export const LogoSettings: React.FC<LogoSettingsProps> = ({}: LogoSettingsProps) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: account, isFetching: isAccountFetching, isSuccess: isAccountFetchingSuccess } = useGetAccountQuery({ accountId })

  const [updateLogoFile, { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading, data: updatedAccount, isError: isUpdateError }] = useUpdateLogoFileMutation()
  const [resetLogoFile, { isLoading: isResetLoading, isError: isResetError }] = useResetLogoFileMutation()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [logoFileInfo, setLogoFileInfo] = useState<FileInfo | null>(account?.data.config.assets?.logo?.logoFile ?? null)

  useEffect(() => {
    setIsLoading(isUpdateLoading || isResetLoading || isAccountFetching)
  }, [isUpdateLoading, isResetLoading, isAccountFetching])

  useEffect(() => {
    if (isUpdateError || isResetError) {
      setIsLoading(false)
    }
  }, [isUpdateError, isResetError])

  useEffect(() => {
    if (isUpdateSuccess && updatedAccount) {
      setLogoFileInfo(updatedAccount.config.assets?.logo?.logoFile ?? null)
    }
  }, [isUpdateSuccess])

  useEffect(() => {
    if (isAccountFetchingSuccess) {
      setLogoFileInfo(account?.data.config.assets?.logo?.logoFile ?? null)
    }
  }, [isAccountFetchingSuccess, account])

  const updateLogoFileConfig = (fileId: string): void => {
    setIsLoading(true)
    updateLogoFile({
      accountId,
      body: {
        fileId,
      },
    })
  }

  const resetFileCallback = (): void => {
    setLogoFileInfo(null)
    resetLogoFile({
      accountId,
    })
  }

  return (
    <Container>
      <Row className='zocks-mb-2'>
        <Col>
          <h4 className='zocks-text-gray-900 mb-2'>Account logo</h4>
        </Col>
      </Row>
      <Row className='mb-2'>
        <Col>
          <p className='zocks-text-gray-900 p-0 m-0'>Add your company logo to personalize your account.</p>
          <p className='zocks-text-gray-900 p-0 m-0'>Accepts PNG, JPG, or GIF • Minimum height: 160 px • Up to 5 MB.</p>
        </Col>
      </Row>
      <Row>
        <Col className='pe-2'>
          <FileHandler
            loading={isLoading}
            enabled={!isLoading}
            fileType={'image'}
            fileKind={FileKind.LOGO_ASSET}
            fileInfo={logoFileInfo}
            onFileUploaded={updateLogoFileConfig}
            onFileReset={resetFileCallback}
            maxFileSize={_5_MB_IN_BYTES}
            buttonBackgroundImage={imageIcon}
            supportedMimeTypes='image/png,image/jpeg,image/gif'
            scope={ACCOUNT_SCOPE}
            defaultFile={DEFAULT_FILE}
            imageMinResolution={LOGO_MIN_RESOLUTION}
          />
        </Col>
      </Row>
    </Container>
  )
}
